import React, { useEffect, useState } from "react";
import { DatePicker } from "antd";
import moment from "moment";

import V2PageHeader from "../../../v2/components/V2PageHeader";
import V2Card from "../../../v2/components/V2Card";
import ChecklistMatrixTable from "../shared/ChecklistMatrixTable";
import ChecklistStatusLegend from "../shared/ChecklistStatusLegend";
import useChecklistStatusFilter from "../shared/useChecklistStatusFilter";
import { getAdminMatrix, exportChecklist } from "../../../helpers/checklistApi";
import { adaptAdminMatrix, getAdminMatrixSummary } from "../shared/adaptAdminMatrix";

import "./index.css";

const TODAY = moment();
const EMPTY_MATRIX = { stores: [], items: [], sectionSummary: [] };

export default function AdminMatrixView() {
  const [selectedDate, setSelectedDate] = useState(TODAY);
  const [loading, setLoading] = useState(true);
  const [matrix, setMatrix] = useState(EMPTY_MATRIX);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getAdminMatrix(selectedDate.format("YYYY-MM-DD")).then((data) => {
      if (cancelled) return;
      setMatrix(adaptAdminMatrix(data));
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedDate]);

  const { stores, items, sectionSummary } = matrix;
  const storeIds = stores.map((store) => store.id);
  const { activeStatuses, toggleStatus, visibleItemCount, totalItemCount } = useChecklistStatusFilter(
    items,
    storeIds
  );
  const summary = getAdminMatrixSummary(stores, items);
  const hasData = !loading && stores.length > 0 && items.length > 0;

  const handleExport = async (format) => {
    setExporting(true);
    await exportChecklist({ format, date: selectedDate.format("YYYY-MM-DD") });
    setExporting(false);
  };

  return (
    <div className="v2-scope admin-matrix-view">
      <V2PageHeader
        title="Store checklists (Admin View)"
        subtitle="Matrix view · Every checklist, every store, in one place."
        action={
          <div className="admin-header-actions">
            <button
              type="button"
              className="admin-export-button"
              onClick={() => handleExport("excel")}
              disabled={exporting}
            >
              Export Excel
            </button>
            <button
              type="button"
              className="admin-export-button"
              onClick={() => handleExport("pdf")}
              disabled={exporting}
            >
              Export PDF
            </button>
            <DatePicker
              className="v2-date-picker"
              value={selectedDate}
              onChange={(date) => date && setSelectedDate(date)}
              format="DD MMM YYYY"
              allowClear={false}
              inputReadOnly
              disabledDate={(current) => current && current.isAfter(TODAY, "day")}
              suffixIcon={<span className="v2-date-picker-caret">▾</span>}
            />
          </div>
        }
      />

      {(loading || hasData) && (
        <>
          <div className="admin-summary-row">
            <V2Card
              value={summary.submittedStores}
              label="Submitted"
              tone="green"
              className="admin-summary-card-narrow"
              loading={loading}
            />
            <V2Card value={summary.pendingStores} label="Pending" tone="default" loading={loading} />
            <V2Card value={summary.flaggedItems} label="Flagged items" tone="red" loading={loading} />
            <V2Card value={summary.pendingItems} label="Pending items" tone="muted" loading={loading} />
            <V2Card value={summary.doneItems} label="Done items" tone="blue" loading={loading} />
          </div>

          {!loading && (
            <ChecklistStatusLegend
              activeStatuses={activeStatuses}
              onToggleStatus={toggleStatus}
              visibleItemCount={visibleItemCount}
              totalItemCount={totalItemCount}
              storeCount={stores.length}
            />
          )}

          <ChecklistMatrixTable
            loading={loading}
            stores={stores}
            items={items}
            sectionSummary={sectionSummary}
            activeStatuses={activeStatuses}
          />
        </>
      )}

      {!loading && !hasData && (
        <div className="admin-empty-state">
          <p className="admin-empty-state-title">No checklist data for {selectedDate.format("DD MMM YYYY")}</p>
          <p className="admin-empty-state-hint">
            No stores or items were returned for this date. If this keeps happening, check that you're logged in
            with an admin account and that the checklist API host in src/config/index.js is correct.
          </p>
        </div>
      )}
    </div>
  );
}
